import { useState, useEffect, useRef, useCallback } from "react";
import GlobalStyles from "../components/GlobalStyles";
import AuthModal from "../components/AuthModal";

// ── Datos estáticos ─────────────────────────────────────────────────────────
const TESTIMONIALS = [
  { color: "#4A7FB5", letter: "M", text: "Nunca había tenido una herramienta que me dijera exactamente qué preguntas no debí hacer. LitisLab cambió cómo preparo mis contrainterrogatorios.", name: "María Fernanda Ríos", role: "Defensora pública · Guayaquil" },
  { color: "#70A33C", letter: "C", text: "La IA es un adversario de verdad. No te deja cometer errores sin cobrarlos. Después de un mes, mi técnica mejoró notablemente en sala real.", name: "Carlos Estrada", role: "Abogado penalista · Quito" },
  { color: "#C4572A", letter: "A", text: "Llegué a mi primera pasantía habiendo practicado decenas de interrogatorios. Mis supervisores no podían creer que era estudiante de tercer año.", name: "Andrea Lozano", role: "Estudiante de Derecho · PUCE" },
  { color: "#A07810", letter: "R", text: "Usamos LitisLab para entrenar a los asociados antes de que entren a sala. Es el estándar de preparación de nuestra firma ahora.", name: "Roberto Almeida", role: "Socio fundador · Almeida & Paredes" },
  { color: "#5B5BA8", letter: "V", text: "Los casos generados son increíblemente reales. He encontrado tipos de hechos que ya he enfrentado en mis audiencias. Preparación práctica de primer nivel.", name: "Valeria Moreno", role: "Fiscal · Unidad de Flagrancia" },
];

const FAQ_ITEMS = [
  { q: "¿Necesito conocimientos técnicos para usar LitisLab?", a: "No. LitisLab está diseñado para abogados, estudiantes y personas sin formación técnica. Si puedes escribir una pregunta en un chat, puedes practicar litigación oral con nosotros." },
  { q: "¿Los casos están basados en el COIP vigente?", a: "Sí. Todos los casos, tipificaciones y procedimientos están basados en el Código Orgánico Integral Penal actualizado de Ecuador y el sistema acusatorio oral vigente." },
  { q: "¿Cómo funciona la IA como contraparte?", a: "La IA adopta el rol de testigo, perito o fiscal según el escenario. Responde con información coherente al expediente, plantea objeciones procesales y evalúa la calidad de cada pregunta en tiempo real." },
  { q: "¿Puedo practicar tanto como fiscal como defensor?", a: "Sí. En cada caso puedes elegir practicar desde la posición de Fiscalía o Defensa. La IA adapta su comportamiento y las teorías del caso al rol que elijas." },
  { q: "¿Cuándo tendrá más funcionalidades?", a: "LitisLab está en desarrollo activo. Próximamente: alegatos de apertura y clausura, modo competitivo con ranking, biblioteca jurídica integrada y más tipos de delitos del COIP." },
  { q: "¿Hay un plan gratuito?", a: "Sí. Puedes comenzar a practicar de forma gratuita. El acceso gratuito incluye simulaciones de interrogatorio y contrainterrogatorio con expedientes generados por IA." },
];

// ── CSS completo de la landing — inyectado vía <style> tag ──────────────────
const LANDING_CSS = `
.litis-landing {
  --bg:      #ECEAE3;
  --bg2:     #E3E0D8;
  --white:   #F9F8F5;
  --dark:    #1A170F;
  --text:    #2C2820;
  --muted:   #7A7268;
  --light:   #B0AA9E;
  --orange:  #C4572A;
  --orange2: #A8451C;
  --green:   #70A33C;
  --border:  rgba(30,26,20,0.10);
  --accent:  #C4572A;
  --accent2: #A8451C;
  --sans:    'Fira Sans', system-ui, sans-serif;
  --radius:  12px;
  --radius-lg: 20px;
  background: var(--bg);
  color: var(--text);
  font-family: var(--sans);
  font-weight: 400;
  -webkit-font-smoothing: antialiased;
  overflow-x: hidden;
  min-height: 100vh;
}
.litis-landing[data-team="green"] {
  --accent:  #70A33C;
  --accent2: #558A28;
}
.litis-landing *, .litis-landing *::before, .litis-landing *::after {
  box-sizing: border-box; margin: 0; padding: 0;
}

/* ── NAV ── */
.litis-landing nav {
  position: sticky; top: 0; z-index: 100;
  background: rgba(236,234,227,0.95);
  backdrop-filter: blur(20px);
  -webkit-backdrop-filter: blur(20px);
  border-bottom: 1px solid var(--border);
  height: 72px;
  display: flex; align-items: center;
  padding: 0 48px;
  gap: 48px;
}
.litis-landing .nav-logo {
  display: flex; align-items: center; text-decoration: none; flex-shrink: 0;
  font-family: 'Fira Sans', sans-serif; font-size: 22px; font-weight: 700;
  color: var(--dark); letter-spacing: -0.02em;
  background: none; border: none; padding: 0; cursor: pointer;
}
.litis-landing .nav-logo span { color: var(--accent); }
.litis-landing .nav-logo-img { height: 30px; width: auto; display: block; }
.litis-landing .nav-links { display: flex; align-items: center; gap: 4px; flex: 1; justify-content: center; }
.litis-landing .nav-links a {
  font-size: 16px; font-weight: 500; color: var(--muted);
  text-decoration: none; padding: 8px 18px; border-radius: 8px;
  transition: color 0.2s, background 0.2s;
  letter-spacing: -0.01em;
  background: none; border: none; cursor: pointer;
  font-family: var(--sans);
}
.litis-landing .nav-links a:hover { color: var(--text); background: rgba(30,26,20,0.07); }
.litis-landing .nav-right { display: flex; align-items: center; gap: 14px; flex-shrink: 0; }
.litis-landing .btn-ghost {
  font-family: var(--sans); font-size: 15px; font-weight: 500;
  color: var(--text); background: none; border: none;
  padding: 9px 18px; border-radius: 8px; cursor: pointer;
  text-decoration: none; transition: background 0.2s;
}
.litis-landing .btn-ghost:hover { background: rgba(30,26,20,0.08); }
.litis-landing .btn-dark {
  font-family: var(--sans); font-size: 15px; font-weight: 500;
  color: var(--white); background: var(--dark);
  border: none; padding: 11px 24px; border-radius: 8px;
  cursor: pointer; text-decoration: none;
  transition: opacity 0.2s, transform 0.15s;
  display: inline-flex; align-items: center; gap: 6px;
}
.litis-landing .btn-dark:hover { opacity: 0.85; transform: translateY(-1px); }
.litis-landing .btn-arrow::after { content: '›'; font-size: 17px; }

/* ── HERO ── */
.litis-landing .hero {
  text-align: center;
  padding: 80px 24px 0;
  max-width: 860px;
  margin: 0 auto;
}
.litis-landing .hero-title {
  font-size: clamp(40px, 5.5vw, 72px);
  font-weight: 500;
  line-height: 1.05;
  letter-spacing: -0.03em;
  color: var(--dark);
  margin-bottom: 20px;
}
.litis-landing .hero-title .accent { color: var(--accent); }
.litis-landing .hero-sub {
  font-size: clamp(16px, 1.8vw, 20px);
  color: var(--muted); font-weight: 400; line-height: 1.65;
  max-width: 580px; margin: 0 auto 36px;
}
.litis-landing .hero-actions {
  display: flex; align-items: center; justify-content: center; gap: 12px;
  margin-bottom: 72px; flex-wrap: wrap;
}
.litis-landing .btn-orange {
  font-family: var(--sans); font-size: 15px; font-weight: 500;
  color: white; background: var(--accent);
  border: none; padding: 12px 26px; border-radius: 10px;
  cursor: pointer; text-decoration: none;
  transition: background 0.2s, transform 0.15s;
  display: inline-flex; align-items: center; gap: 6px;
}
.litis-landing .btn-orange:hover { background: var(--accent2); transform: translateY(-1px); }
.litis-landing .btn-outline {
  font-family: var(--sans); font-size: 15px; font-weight: 500;
  color: var(--text); background: var(--white);
  border: 1px solid var(--border); padding: 12px 26px; border-radius: 10px;
  cursor: pointer; text-decoration: none;
  transition: border-color 0.2s, transform 0.15s;
  display: inline-flex; align-items: center; gap: 6px;
}
.litis-landing .btn-outline:hover { border-color: rgba(30,26,20,0.25); transform: translateY(-1px); }

/* ── HERO VISUAL ── */
.litis-landing .hero-visual-wrap { max-width: 1100px; margin: 0 auto; padding: 0 40px 0; }
.litis-landing .hero-visual {
  background: var(--white);
  border: 1px solid var(--border);
  border-radius: var(--radius-lg);
  overflow: hidden;
  box-shadow: 0 8px 48px rgba(30,26,20,0.10), 0 1px 2px rgba(30,26,20,0.06);
  margin: 0 -40px 0;
  position: relative;
}
.litis-landing .hero-visual-bar {
  background: var(--bg2);
  border-bottom: 1px solid var(--border);
  padding: 12px 16px;
  display: flex; align-items: center; gap: 8px;
}
.litis-landing .window-dot { width: 12px; height: 12px; border-radius: 50%; flex-shrink: 0; }
.litis-landing .hero-visual-url {
  flex: 1; text-align: center;
  font-size: 12px; color: var(--light);
  background: var(--bg);
  border-radius: 6px; padding: 4px 12px;
  max-width: 280px; margin: 0 auto;
}

/* App mockup inside hero */
.litis-landing .app-mock {
  background: var(--bg);
  min-height: 420px;
  display: grid;
  grid-template-columns: 220px 1fr;
  grid-template-rows: 48px 1fr;
  font-size: 13px;
}
.litis-landing .app-topbar {
  grid-column: 1 / -1;
  background: var(--dark);
  display: flex; align-items: center;
  padding: 0 16px; gap: 12px;
  border-bottom: 1px solid rgba(255,255,255,0.06);
}
.litis-landing .app-topbar-logo {
  font-size: 14px; font-weight: 700; color: white;
  display: flex; align-items: center; gap: 8px;
}
.litis-landing .app-topbar-logo .brand-mark {
  font-size: 14px; font-weight: 700; letter-spacing: -0.02em;
}
.litis-landing .app-topbar-logo .brand-mark span { color: var(--accent); }
.litis-landing .app-topbar-case {
  font-size: 12px; color: rgba(255,255,255,0.45);
  margin-left: 8px;
}
.litis-landing .app-topbar-right {
  margin-left: auto; display: flex; align-items: center; gap: 10px;
}
.litis-landing .app-badge {
  font-size: 11px; font-weight: 500; letter-spacing: 0.05em;
  padding: 3px 10px; border-radius: 4px; text-transform: uppercase;
}
.litis-landing .badge-int { background: rgba(196,87,42,0.25); color: #E88060; }
.litis-landing .badge-timer { color: rgba(255,255,255,0.5); font-size: 12px; font-family: monospace; }
.litis-landing .app-sidebar {
  background: var(--white);
  border-right: 1px solid var(--border);
  padding: 16px 12px;
  display: flex; flex-direction: column; gap: 20px;
}
.litis-landing .sidebar-section-label {
  font-size: 10px; font-weight: 500; letter-spacing: 0.1em;
  text-transform: uppercase; color: var(--light); margin-bottom: 8px;
}
.litis-landing .witness-active {
  background: var(--bg);
  border: 1px solid var(--border);
  border-radius: 8px; padding: 10px;
}
.litis-landing .witness-name { font-size: 13px; font-weight: 500; color: var(--dark); }
.litis-landing .witness-role { font-size: 11px; color: var(--muted); margin-top: 2px; }
.litis-landing .witness-tag {
  display: inline-block; margin-top: 6px;
  font-size: 10px; font-weight: 500; letter-spacing: 0.06em;
  padding: 2px 8px; border-radius: 4px; text-transform: uppercase;
  background: rgba(112,163,60,0.15); color: var(--green);
}
.litis-landing .witness-list { display: flex; flex-direction: column; gap: 6px; }
.litis-landing .witness-item {
  display: flex; align-items: center; gap: 8px;
  font-size: 12px; color: var(--muted); padding: 4px 6px;
  border-radius: 6px;
}
.litis-landing .witness-dot { width: 8px; height: 8px; border-radius: 50%; flex-shrink: 0; }
.litis-landing .theory-box {
  background: var(--bg);
  border-radius: 8px; padding: 10px;
  font-size: 11px; color: var(--muted); line-height: 1.55;
}
.litis-landing .theory-label {
  font-size: 10px; font-weight: 700; letter-spacing: 0.06em;
  text-transform: uppercase; color: var(--accent); margin-bottom: 4px;
}
.litis-landing .app-main {
  display: flex; flex-direction: column;
  background: var(--bg);
}
.litis-landing .transcript {
  flex: 1; padding: 20px 24px;
  display: flex; flex-direction: column; gap: 0;
  overflow: hidden;
}
.litis-landing .t-msg { padding: 12px 0; border-bottom: 1px solid var(--border); }
.litis-landing .t-msg:last-child { border-bottom: none; }
.litis-landing .t-role {
  font-size: 10px; font-weight: 700; letter-spacing: 0.08em;
  text-transform: uppercase; margin-bottom: 5px;
}
.litis-landing .t-role.sec { color: var(--light); }
.litis-landing .t-role.fis { color: #5A8FC4; }
.litis-landing .t-role.test { color: var(--green); }
.litis-landing .t-role.def { color: var(--accent); }
.litis-landing .t-text { font-size: 13px; line-height: 1.65; color: var(--text); }
.litis-landing .t-text em { color: var(--muted); font-style: italic; }
.litis-landing .app-input {
  background: var(--white);
  border-top: 1px solid var(--border);
  padding: 12px 16px;
  display: flex; align-items: center; gap: 10px;
  flex-shrink: 0;
}
.litis-landing .app-input-field {
  flex: 1; font-size: 13px; color: var(--muted);
  background: var(--bg); border: 1px solid var(--border);
  border-radius: 8px; padding: 9px 14px; font-family: var(--sans);
}
.litis-landing .app-send {
  background: var(--dark); color: white;
  border: none; border-radius: 8px; padding: 9px 18px;
  font-size: 13px; font-weight: 500; cursor: pointer; font-family: var(--sans);
}

/* ── LOGOS STRIP ── */
.litis-landing .logos-strip { padding: 52px 24px; text-align: center; border-bottom: 1px solid var(--border); }
.litis-landing .logos-strip-label {
  font-size: 13px; color: var(--light); font-weight: 500;
  margin-bottom: 24px; letter-spacing: 0.02em;
}
.litis-landing .logos-row {
  display: flex; align-items: center; justify-content: center;
  gap: 48px; flex-wrap: wrap;
}
.litis-landing .logo-pill {
  font-size: 14px; font-weight: 700;
  letter-spacing: -0.01em;
  background: var(--bg2);
  padding: 8px 20px; border-radius: 8px;
  color: var(--light);
}

/* ── FEATURE SECTIONS ── */
.litis-landing .feature-section {
  max-width: 1160px; margin: 0 auto;
  padding: 100px 40px;
  display: grid; grid-template-columns: 1fr 1fr;
  gap: 80px; align-items: center;
}
.litis-landing .feature-section.reverse { direction: rtl; }
.litis-landing .feature-section.reverse > * { direction: ltr; }
.litis-landing .feature-tag {
  display: inline-block;
  font-size: 13px; font-weight: 500; color: var(--accent);
  margin-bottom: 12px;
}
.litis-landing .feature-title {
  font-size: clamp(26px, 2.8vw, 38px);
  font-weight: 500; line-height: 1.1; letter-spacing: -0.03em;
  color: var(--dark); margin-bottom: 16px;
}
.litis-landing .feature-desc {
  font-size: 16px; color: var(--muted); line-height: 1.7;
  font-weight: 400; margin-bottom: 28px; max-width: 440px;
}
.litis-landing .btn-dark-sm {
  font-family: var(--sans); font-size: 14px; font-weight: 500;
  color: var(--white); background: var(--dark);
  border: none; padding: 10px 22px; border-radius: 8px;
  cursor: pointer; text-decoration: none;
  display: inline-flex; align-items: center; gap: 6px;
  transition: opacity 0.2s, transform 0.15s;
}
.litis-landing .btn-dark-sm:hover { opacity: 0.8; transform: translateY(-1px); }
.litis-landing .feature-visual {
  background: var(--white);
  border: 1px solid var(--border);
  border-radius: var(--radius-lg);
  overflow: hidden;
  box-shadow: 0 4px 32px rgba(30,26,20,0.08);
  aspect-ratio: 4/3;
  display: flex; flex-direction: column;
}
.litis-landing .fv-bar {
  background: var(--bg2); border-bottom: 1px solid var(--border);
  padding: 10px 14px; display: flex; align-items: center; gap: 6px; flex-shrink: 0;
}
.litis-landing .fv-body { flex: 1; padding: 20px; overflow: hidden; }

/* Feature 1: Setup flow */
.litis-landing .setup-steps { display: flex; flex-direction: column; gap: 8px; }
.litis-landing .setup-step {
  background: var(--bg); border: 1px solid var(--border); border-radius: 8px;
  padding: 12px 16px; display: flex; align-items: center; gap: 12px;
}
.litis-landing .setup-step.active { border-color: var(--accent); background: var(--bg2); }
.litis-landing .setup-step-num {
  width: 24px; height: 24px; border-radius: 50%; flex-shrink: 0;
  background: #D8D4CB; display: flex; align-items: center; justify-content: center;
  font-size: 11px; font-weight: 700; color: var(--muted);
}
.litis-landing .setup-step.active .setup-step-num { background: var(--accent); color: white; }
.litis-landing .setup-step-label { font-size: 13px; font-weight: 500; color: var(--text); }
.litis-landing .setup-step-sub { font-size: 12px; color: var(--muted); }
.litis-landing .setup-choices { display: grid; grid-template-columns: 1fr 1fr; gap: 6px; margin-top: 12px; }
.litis-landing .setup-choice {
  background: var(--bg); border: 1px solid var(--border);
  border-radius: 8px; padding: 10px 12px;
}
.litis-landing .setup-choice.sel { background: var(--accent); border-color: var(--accent); }
.litis-landing .setup-choice-title { font-size: 12px; font-weight: 700; color: var(--text); }
.litis-landing .setup-choice.sel .setup-choice-title { color: white; }
.litis-landing .setup-choice-sub { font-size: 11px; color: var(--muted); }
.litis-landing .setup-choice.sel .setup-choice-sub { color: rgba(255,255,255,0.65); }

/* Feature 2: Case document */
.litis-landing .case-doc { font-size: 12px; line-height: 1.6; }
.litis-landing .case-doc-title { font-size: 15px; font-weight: 700; color: var(--dark); margin-bottom: 4px; }
.litis-landing .case-doc-meta { font-size: 11px; color: var(--muted); margin-bottom: 12px; }
.litis-landing .case-doc-body { color: var(--text); font-size: 12px; line-height: 1.65; margin-bottom: 14px; }
.litis-landing .theory-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 8px; }
.litis-landing .theory-card {
  background: var(--bg); border-radius: 8px; padding: 10px 12px;
  border: 1px solid var(--border);
}
.litis-landing .theory-card-label { font-size: 10px; font-weight: 700; letter-spacing: 0.06em; text-transform: uppercase; margin-bottom: 6px; }
.litis-landing .theory-card-label.fis { color: #5A8FC4; }
.litis-landing .theory-card-label.def { color: var(--accent); }
.litis-landing .theory-card-text { font-size: 11px; color: var(--muted); line-height: 1.55; }

/* Feature 3: Score report */
.litis-landing .score-report { display: flex; flex-direction: column; gap: 12px; }
.litis-landing .score-row { display: flex; flex-direction: column; gap: 6px; }
.litis-landing .score-row-top { display: flex; justify-content: space-between; align-items: baseline; }
.litis-landing .score-row-label { font-size: 12px; font-weight: 500; color: var(--text); }
.litis-landing .score-row-val { font-size: 16px; font-weight: 500; color: var(--dark); }
.litis-landing .score-bar { height: 6px; background: var(--bg2); border-radius: 3px; overflow: hidden; }
.litis-landing .score-fill { height: 100%; border-radius: 3px; transition: width 1.5s ease; }
.litis-landing .score-comment { font-size: 11px; color: var(--muted); line-height: 1.45; }
.litis-landing .score-total {
  background: var(--dark); border-radius: 10px; padding: 14px 16px;
  display: flex; justify-content: space-between; align-items: center;
  margin-top: 4px;
}
.litis-landing .score-total-label { font-size: 13px; font-weight: 500; color: rgba(255,255,255,0.65); }
.litis-landing .score-total-val { font-size: 26px; font-weight: 500; color: white; }

/* ── DIVIDER ── */
.litis-landing .section-divider { border: none; border-top: 1px solid var(--border); margin: 0; }

/* ── TEAM TOGGLE ── */
.litis-landing .team-toggle { display: flex; align-items: center; gap: 6px; flex-shrink: 0; }
.litis-landing .team-btn {
  width: 28px; height: 28px; border-radius: 50%;
  border: 2.5px solid transparent;
  cursor: pointer; background: none; padding: 0;
  transition: all 0.2s; position: relative;
  display: flex; align-items: center; justify-content: center;
}
.litis-landing .team-btn:hover { transform: scale(1.12); }
.litis-landing .team-btn.active { border-color: var(--dark); box-shadow: 0 0 0 3px rgba(30,26,20,0.10); }
.litis-landing .team-btn-inner {
  width: 20px; height: 20px; border-radius: 50%;
  transition: transform 0.2s;
}

/* ── HAMBURGUESA Y PANEL MOBILE ── */
.litis-landing .nav-hamburger {
  display: none;  /* solo visible en mobile, ver media query */
  flex-direction: column; justify-content: center; align-items: center;
  width: 40px; height: 40px; padding: 0; gap: 5px;
  background: none; border: 1px solid var(--border); border-radius: 8px;
  cursor: pointer; flex-shrink: 0;
  transition: border-color 0.15s;
}
.litis-landing .nav-hamburger:hover { border-color: rgba(30,26,20,0.25); }
.litis-landing .nav-hamburger span {
  display: block; width: 18px; height: 2px;
  background: var(--dark); border-radius: 2px;
}

.litis-landing .mobile-menu {
  position: fixed; inset: 0; z-index: 1000;
  background: rgba(15,12,8,0); pointer-events: none;
  transition: background 0.25s ease;
}
.litis-landing .mobile-menu.open {
  background: rgba(15,12,8,0.45); pointer-events: auto;
  backdrop-filter: blur(4px); -webkit-backdrop-filter: blur(4px);
}
.litis-landing .mobile-menu-panel {
  position: absolute; top: 0; right: 0; bottom: 0;
  width: min(86%, 340px);
  background: var(--bg);
  display: flex; flex-direction: column;
  padding: 20px 22px 32px;
  transform: translateX(100%);
  transition: transform 0.3s cubic-bezier(.22,1,.36,1);
  box-shadow: -16px 0 40px rgba(0,0,0,0.18);
  overflow-y: auto;
}
.litis-landing .mobile-menu.open .mobile-menu-panel { transform: translateX(0); }
.litis-landing .mobile-menu-head {
  display: flex; align-items: center; justify-content: space-between;
  margin-bottom: 28px;
}
.litis-landing .mobile-menu-logo { height: 26px; width: auto; display: block; }
.litis-landing .mobile-menu-close {
  width: 36px; height: 36px; border-radius: 50%;
  background: var(--bg2); border: none; cursor: pointer;
  font-size: 18px; color: var(--muted);
  display: flex; align-items: center; justify-content: center;
  transition: background 0.15s;
}
.litis-landing .mobile-menu-close:hover { background: var(--bg3); color: var(--dark); }

.litis-landing .mobile-menu-links {
  display: flex; flex-direction: column;
  border-bottom: 1px solid var(--border);
  padding-bottom: 14px; margin-bottom: 24px;
}
.litis-landing .mobile-menu-links a {
  font-family: 'Fira Sans', sans-serif; font-size: 17px; font-weight: 500;
  color: var(--dark); text-decoration: none;
  padding: 14px 0;
  border-bottom: 1px solid var(--border2);
}
.litis-landing .mobile-menu-links a:last-child { border-bottom: none; }
.litis-landing .mobile-menu-links a:active { color: var(--accent); }

.litis-landing .mobile-menu-label {
  font-size: 11px; font-weight: 700; letter-spacing: 0.1em;
  text-transform: uppercase; color: var(--light); margin-bottom: 10px;
}
.litis-landing .mobile-menu-team { margin-bottom: 26px; }
.litis-landing .mobile-menu-team-row { display: flex; gap: 10px; }
.litis-landing .mobile-team-card {
  flex: 1; display: flex; align-items: center; gap: 10px;
  background: var(--white); border: 1px solid var(--border);
  border-radius: 10px; padding: 12px 14px; cursor: pointer;
  font-family: 'Fira Sans', sans-serif; font-size: 14px; font-weight: 500;
  color: var(--dark);
  transition: border-color 0.15s, background 0.15s;
}
.litis-landing .mobile-team-card.active { border-color: var(--dark); background: var(--bg2); }
.litis-landing .mobile-team-dot {
  width: 18px; height: 18px; border-radius: 50%; flex-shrink: 0;
}

.litis-landing .mobile-menu-actions {
  display: flex; flex-direction: column; gap: 10px;
  margin-top: auto;  /* empuja al fondo */
}
.litis-landing .mobile-menu-btn-ghost {
  font-family: 'Fira Sans', sans-serif; font-size: 14px; font-weight: 500;
  background: none; color: var(--dark);
  border: 1px solid var(--border); border-radius: 10px;
  padding: 14px 18px; cursor: pointer;
  transition: border-color 0.15s;
}
.litis-landing .mobile-menu-btn-ghost:hover { border-color: rgba(30,26,20,0.25); }
.litis-landing .mobile-menu-btn-dark {
  font-family: 'Fira Sans', sans-serif; font-size: 14px; font-weight: 500;
  background: var(--dark); color: white;
  border: none; border-radius: 10px;
  padding: 14px 18px; cursor: pointer;
  transition: opacity 0.15s;
}
.litis-landing .mobile-menu-btn-dark:hover { opacity: 0.88; }

/* ── TESTIMONIALS ── */
.litis-landing .testimonials { padding: 100px 0; overflow: hidden; }
.litis-landing .testimonials-header { text-align: center; margin-bottom: 52px; padding: 0 40px; }
.litis-landing .section-title {
  font-size: clamp(28px, 3.5vw, 48px);
  font-weight: 500; letter-spacing: -0.03em;
  color: var(--dark); margin-bottom: 12px;
}
.litis-landing .section-title .accent { color: var(--accent); }
.litis-landing .section-sub { font-size: 17px; color: var(--muted); }
.litis-landing .testimonials-track {
  display: flex; gap: 20px;
  animation: litis-scroll-left 40s linear infinite;
  width: max-content; padding: 0 40px;
}
.litis-landing .testimonials-track:hover { animation-play-state: paused; }
@keyframes litis-scroll-left {
  from { transform: translateX(0); }
  to   { transform: translateX(-50%); }
}
.litis-landing .testimonial-card {
  background: var(--white); border: 1px solid var(--border);
  border-radius: var(--radius-lg); padding: 32px 28px;
  width: 340px; flex-shrink: 0;
  transition: transform 0.3s ease, box-shadow 0.3s ease;
}
.litis-landing .testimonial-card:hover { transform: translateY(-4px); box-shadow: 0 12px 40px rgba(30,26,20,0.10); }
.litis-landing .test-avatar {
  width: 40px; height: 40px; border-radius: 50%; flex-shrink: 0;
  display: flex; align-items: center; justify-content: center;
  font-size: 15px; font-weight: 700; color: white; margin-bottom: 16px;
}
.litis-landing .test-quote {
  font-size: 22px; color: var(--accent); opacity: 0.3;
  line-height: 1; margin-bottom: 8px; font-family: Georgia, serif;
}
.litis-landing .test-text { font-size: 15px; color: var(--text); line-height: 1.7; margin-bottom: 20px; font-style: italic; }
.litis-landing .test-byline { display: flex; align-items: center; gap: 10px; padding-top: 16px; border-top: 1px solid var(--border); }
.litis-landing .test-name { font-size: 14px; font-weight: 700; color: var(--dark); }
.litis-landing .test-role { font-size: 12px; color: var(--muted); margin-top: 1px; }

/* ── FAQ ── */
.litis-landing .faq-section { max-width: 780px; margin: 0 auto; padding: 100px 40px; }
.litis-landing .faq-title { font-size: clamp(28px, 3.5vw, 48px); font-weight: 500; letter-spacing: -0.03em; color: var(--dark); margin-bottom: 40px; text-align: center; }
.litis-landing .faq-item { border-top: 1px solid var(--border); }
.litis-landing .faq-item:last-child { border-bottom: 1px solid var(--border); }
.litis-landing .faq-q {
  display: flex; justify-content: space-between; align-items: center;
  padding: 20px 0; cursor: pointer; gap: 16px;
  font-size: 15px; font-weight: 500; color: var(--dark);
  background: none; border: none; width: 100%; text-align: left;
  font-family: var(--sans);
}
.litis-landing .faq-q:hover { color: var(--orange); }
.litis-landing .faq-icon { font-size: 18px; color: var(--muted); flex-shrink: 0; transition: transform 0.2s; }
.litis-landing .faq-item.open .faq-icon { transform: rotate(45deg); color: var(--orange); }
.litis-landing .faq-a {
  font-size: 14px; color: var(--muted); line-height: 1.7;
  overflow: hidden; max-height: 0; transition: max-height 0.3s ease, padding 0.3s;
  padding-bottom: 0;
}
.litis-landing .faq-item.open .faq-a { max-height: 200px; padding-bottom: 20px; }

/* ── CTA SECTION ── */
.litis-landing .cta-section {
  background: var(--dark);
  margin: 0 40px 80px; border-radius: var(--radius-lg);
  padding: 80px 60px; text-align: center;
}
.litis-landing .cta-title {
  font-size: clamp(32px, 4vw, 56px); font-weight: 500;
  letter-spacing: -0.03em; color: white; margin-bottom: 16px;
}
.litis-landing .cta-title .accent { color: var(--accent); }
.litis-landing .cta-sub { font-size: 17px; color: rgba(255,255,255,0.5); margin-bottom: 36px; }
.litis-landing .cta-actions { display: flex; justify-content: center; gap: 12px; flex-wrap: wrap; }
.litis-landing .cta-link-secondary {
  font-family: var(--sans); font-size: 15px; font-weight: 500;
  color: rgba(255,255,255,0.5); text-decoration: none;
  padding: 12px 26px; border: 1px solid rgba(255,255,255,0.15);
  border-radius: 10px; transition: color 0.2s; cursor: pointer;
  background: none;
}
.litis-landing .cta-link-secondary:hover { color: rgba(255,255,255,0.85); }

/* ── FOOTER ── */
.litis-landing footer {
  background: var(--dark); padding: 60px 60px 40px;
  border-top: 1px solid rgba(255,255,255,0.06);
}
.litis-landing .footer-top {
  display: grid; grid-template-columns: 1.5fr 1fr 1fr 1fr;
  gap: 40px; margin-bottom: 52px;
}
.litis-landing .footer-brand-mark {
  font-size: 22px; font-weight: 700; letter-spacing: -0.02em;
  color: white; margin-bottom: 12px; display: inline-block;
}
.litis-landing .footer-brand-mark span { color: var(--accent); }
.litis-landing .footer-brand-img {
  height: 36px; width: auto; display: block; margin-bottom: 14px;
}
.litis-landing .footer-tagline { font-size: 13px; color: rgba(255,255,255,0.35); line-height: 1.6; max-width: 220px; }
.litis-landing .footer-col-title {
  font-size: 12px; font-weight: 700; letter-spacing: 0.08em;
  text-transform: uppercase; color: rgba(255,255,255,0.3); margin-bottom: 16px;
}
.litis-landing .footer-col-links { display: flex; flex-direction: column; gap: 10px; align-items: flex-start; }
.litis-landing .footer-col-links a,
.litis-landing .footer-col-links button {
  font-size: 14px; color: rgba(255,255,255,0.6); text-decoration: none;
  transition: color 0.2s; cursor: pointer; background: none; border: none;
  font-family: var(--sans); text-align: left; padding: 0;
}
.litis-landing .footer-col-links a:hover,
.litis-landing .footer-col-links button:hover { color: white; }
.litis-landing .footer-bottom {
  border-top: 1px solid rgba(255,255,255,0.07);
  padding-top: 24px; display: flex; justify-content: space-between;
  align-items: center; flex-wrap: wrap; gap: 12px;
}
.litis-landing .footer-copy { font-size: 13px; color: rgba(255,255,255,0.25); }
.litis-landing .footer-bottom-links { display: flex; gap: 20px; }
.litis-landing .footer-bottom-links a,
.litis-landing .footer-bottom-links button {
  font-size: 13px; color: rgba(255,255,255,0.25);
  text-decoration: none; transition: color 0.2s; cursor: pointer;
  background: none; border: none; font-family: var(--sans); padding: 0;
}
.litis-landing .footer-bottom-links a:hover,
.litis-landing .footer-bottom-links button:hover { color: rgba(255,255,255,0.6); }

/* ── REVEAL ── */
.litis-landing .reveal { opacity: 0; transform: translateY(22px); transition: opacity 0.75s cubic-bezier(.22,1,.36,1), transform 0.75s cubic-bezier(.22,1,.36,1); }
.litis-landing .reveal.visible { opacity: 1; transform: none; }

/* ── RIPPLE ── */
.litis-landing .ripple-host { position: relative; overflow: hidden; }
.litis-landing .ripple-wave {
  position: absolute; border-radius: 50%;
  background: rgba(255,255,255,0.28);
  transform: scale(0); pointer-events: none;
  animation: litis-ripple 0.5s ease-out forwards;
}
@keyframes litis-ripple { to { transform: scale(4); opacity: 0; } }

/* Responsive */
/* ── TABLET (≤900px) ── */
@media (max-width: 900px) {
  .litis-landing nav { padding: 0 20px; gap: 20px; height: 64px; }
  .litis-landing .nav-links { display: none; }
  .litis-landing .feature-section { grid-template-columns: 1fr; gap: 40px; padding: 60px 24px; }
  .litis-landing .feature-section.reverse { direction: ltr; }
  .litis-landing .hero-visual { margin: 0; }
  .litis-landing .app-mock { grid-template-columns: 1fr; grid-template-rows: 48px 1fr; }
  .litis-landing .app-sidebar { display: none; }
  .litis-landing .footer-top { grid-template-columns: 1fr 1fr; }
  .litis-landing .cta-section { margin: 0 16px 60px; padding: 52px 28px; }
  .litis-landing footer { padding: 48px 24px 32px; }
  .litis-landing .hero-visual-wrap { padding: 0 16px; }
}

/* ── MOBILE (≤640px) ── */
@media (max-width: 640px) {
  /* NAV: hamburguesa a la derecha + "Comenzar gratis" visible (estilo mulerun) */
  .litis-landing nav {
    padding: 0 16px; height: 60px;
    gap: 0;                          /* el espacio lo da justify-content */
    justify-content: space-between;  /* logo a la izq, nav-right a la der */
  }
  .litis-landing .team-toggle { display: none; }
  .litis-landing .btn-ghost { display: none; }                    /* Iniciar sesión va dentro del menú */
  .litis-landing .btn-dark {                                       /* Comenzar gratis visible, compacto */
    display: inline-flex;
    padding: 8px 14px; font-size: 13px;
    border-radius: 8px; gap: 4px;
  }
  .litis-landing .btn-arrow::after { font-size: 14px; }
  .litis-landing .nav-hamburger { display: flex; }
  .litis-landing .nav-right { gap: 10px; }
  .litis-landing .nav-logo-img { height: 26px; }

  /* HERO: más aire, mejor presencia */
  .litis-landing .hero { padding: 72px 22px 56px; }
  .litis-landing .hero-title { font-size: 38px; line-height: 1.08; letter-spacing: -0.02em; margin-bottom: 22px; }
  .litis-landing .hero-sub { font-size: 16px; line-height: 1.55; margin-bottom: 32px; padding: 0 4px; }
  .litis-landing .hero-actions { flex-direction: column; gap: 12px; align-items: stretch; padding: 0; }
  .litis-landing .hero-actions .btn-orange,
  .litis-landing .hero-actions .btn-outline {
    width: 100%; justify-content: center;
    padding: 15px 24px; font-size: 15px;
    border-radius: 10px;
  }

  /* HERO VISUAL MOCKUP: arreglar superposición de URL/marca interna */
  .litis-landing .hero-visual-wrap { padding: 40px 14px 0; }
  .litis-landing .hero-visual-bar { padding: 0 10px; gap: 6px; }
  .litis-landing .hero-visual-url { font-size: 10px; max-width: 60%; overflow: hidden; white-space: nowrap; text-overflow: ellipsis; }
  .litis-landing .app-mock { grid-template-columns: 1fr; grid-template-rows: auto 1fr; min-height: 420px; }
  .litis-landing .app-sidebar { display: none; }
  .litis-landing .app-topbar { padding: 0 12px; gap: 8px; height: auto; min-height: 44px; flex-wrap: wrap; padding-top: 8px; padding-bottom: 8px; }
  .litis-landing .app-topbar-logo .brand-mark { font-size: 14px; }
  .litis-landing .app-topbar-case-title { font-size: 11px; line-height: 1.3; max-width: 60%; }
  .litis-landing .app-topbar-phase { font-size: 9px; padding: 2px 6px; }
  .litis-landing .app-mock-body { padding: 14px 12px; }
  .litis-landing .mock-msg { padding: 10px 12px; }
  .litis-landing .mock-msg-text { font-size: 12px; line-height: 1.5; }
  .litis-landing .mock-input-bar { padding: 10px 12px; }

  /* SECCIONES INTERIORES */
  .litis-landing .section-eyebrow { font-size: 11px; }
  .litis-landing .section-heading { font-size: 30px; line-height: 1.15; }
  .litis-landing .feature-section { grid-template-columns: 1fr; gap: 32px; padding: 50px 20px; }
  .litis-landing .feature-section.reverse { direction: ltr; }
  .litis-landing .hero-visual { margin: 0; }
  .litis-landing .feature-text h2 { font-size: 26px; line-height: 1.2; }
  .litis-landing .feature-text p { font-size: 15px; }

  /* PRECIOS */
  .litis-landing .pricing-grid { grid-template-columns: 1fr; gap: 14px; }
  .litis-landing .pricing-card { padding: 26px 22px; }
  .litis-landing .pricing-card.featured { transform: none; }

  /* FAQ */
  .litis-landing .faq-section { padding: 60px 20px; }
  .litis-landing .faq-question { font-size: 15px; padding: 18px 0; }
  .litis-landing .faq-answer p { font-size: 14px; }

  /* CTA */
  .litis-landing .cta-section { margin: 0 14px 48px; padding: 40px 22px; }
  .litis-landing .cta-section h2 { font-size: 26px; }
  .litis-landing .cta-section p { font-size: 14px; }
  .litis-landing .cta-section .btn-orange { width: 100%; padding: 13px 20px; font-size: 14px; }

  /* FOOTER */
  .litis-landing footer { padding: 40px 20px 28px; }
  .litis-landing .footer-top { grid-template-columns: 1fr; gap: 28px; }
  .litis-landing .footer-brand-img { height: 30px; }
}

/* ── MOBILE EXTRA-CHICO (≤380px) ── */
@media (max-width: 380px) {
  .litis-landing .hero-title { font-size: 32px; }
  .litis-landing .hero-sub { font-size: 15px; }
  .litis-landing .section-heading { font-size: 26px; }
  .litis-landing .nav-logo-img { height: 22px; }
  .litis-landing .btn-dark { padding: 7px 11px; font-size: 12px; }
  .litis-landing .btn-arrow::after { display: none; }  /* sin flecha en pantallas muy chicas */
  .litis-landing .nav-hamburger { width: 36px; height: 36px; }
  .litis-landing .nav-right { gap: 8px; }
}
`;

// ── Componente principal ────────────────────────────────────────────────────
export default function Landing({ onAuth }) {
  const [team, setTeam]               = useState("orange");
  const [authOpen, setAuthOpen]       = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [authMode, setAuthMode]       = useState("register");
  const [openFaqIdx, setOpenFaqIdx]   = useState(-1);
  const containerRef                  = useRef(null);

  // Cargar Fira Sans desde Google Fonts solo cuando esta página se monte
  useEffect(() => {
    const id = "litis-fira-sans-font";
    if (document.getElementById(id)) return;
    const preconnect1 = document.createElement("link");
    preconnect1.rel = "preconnect";
    preconnect1.href = "https://fonts.googleapis.com";
    document.head.appendChild(preconnect1);
    const preconnect2 = document.createElement("link");
    preconnect2.rel = "preconnect";
    preconnect2.href = "https://fonts.gstatic.com";
    preconnect2.crossOrigin = "anonymous";
    document.head.appendChild(preconnect2);
    const link = document.createElement("link");
    link.id = id;
    link.rel = "stylesheet";
    link.href = "https://fonts.googleapis.com/css2?family=Fira+Sans:wght@300;400;500;600;700&display=swap";
    document.head.appendChild(link);
  }, []);

  // Inicializar team desde localStorage
  useEffect(() => {
    const saved = localStorage.getItem("litislab_team");
    if (saved === "green" || saved === "orange") setTeam(saved);
  }, []);

  // Persistir cambio de team
  const changeTeam = useCallback((nextTeam) => {
    setTeam(nextTeam);
    localStorage.setItem("litislab_team", nextTeam);
  }, []);

  // Scroll reveal con IntersectionObserver
  useEffect(() => {
    const root = containerRef.current;
    if (!root) return;
    const els = root.querySelectorAll(".reveal");
    if (!els.length) return;
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) e.target.classList.add("visible");
        });
      },
      { threshold: 0.05, rootMargin: "0px 0px -40px 0px" }
    );
    els.forEach((el) => io.observe(el));
    // Fallback: si nada visible en 400ms, mostrar todo
    const fallback = setTimeout(() => {
      if (!root.querySelector(".reveal.visible")) {
        els.forEach((el) => el.classList.add("visible"));
      }
    }, 400);
    return () => {
      io.disconnect();
      clearTimeout(fallback);
    };
  }, []);

  // Ripple en clic sobre botones
  const handleRipple = (e) => {
    const el = e.currentTarget;
    el.classList.add("ripple-host");
    const rect = el.getBoundingClientRect();
    const size = Math.max(rect.width, rect.height);
    const x = e.clientX - rect.left - size / 2;
    const y = e.clientY - rect.top - size / 2;
    const wave = document.createElement("span");
    wave.className = "ripple-wave";
    wave.style.cssText = `width:${size}px;height:${size}px;left:${x}px;top:${y}px`;
    el.appendChild(wave);
    wave.addEventListener("animationend", () => wave.remove());
  };

  // Helper: abre el AuthModal en el modo dado y dispara el ripple
  const openAuth = (mode) => (e) => {
    handleRipple(e);
    setAuthMode(mode);
    setAuthOpen(true);
  };

  const toggleFaq = (idx) => setOpenFaqIdx((cur) => (cur === idx ? -1 : idx));

  return (
    <>
      <GlobalStyles />
      <style dangerouslySetInnerHTML={{ __html: LANDING_CSS }} />
      <div ref={containerRef} className="litis-landing" data-team={team}>

        {/* NAV */}
        <nav>
          <button type="button" className="nav-logo" onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })} aria-label="LitisLab inicio">
            <img src="/litislab-logo.png" alt="LitisLab" className="nav-logo-img" />
          </button>
          <div className="nav-links">
            <a href="#funcionalidades">Funcionalidades</a>
            <a href="#proceso">Cómo funciona</a>
            <a href="#precios">Precios</a>
            <a href="#faq">FAQ</a>
          </div>
          <div className="nav-right">
            <div className="team-toggle" title="Color de tema">
              <button
                className={`team-btn ${team === "orange" ? "active" : ""}`}
                onClick={() => changeTeam("orange")}
                title="Tema naranja"
                aria-label="Tema naranja"
              >
                <div className="team-btn-inner" style={{ background: "#C4572A" }} />
              </button>
              <button
                className={`team-btn ${team === "green" ? "active" : ""}`}
                onClick={() => changeTeam("green")}
                title="Tema verde"
                aria-label="Tema verde"
              >
                <div className="team-btn-inner" style={{ background: "#70A33C" }} />
              </button>
            </div>
            <button className="btn-ghost" onClick={openAuth("login")}>Iniciar sesión</button>
            <button className="btn-dark btn-arrow" onClick={openAuth("register")}>Comenzar gratis</button>

            {/* Botón hamburguesa — solo visible en mobile */}
            <button
              className="nav-hamburger"
              aria-label="Abrir menú"
              onClick={() => setMobileMenuOpen(true)}
            >
              <span /><span /><span />
            </button>
          </div>
        </nav>

        {/* Mobile menu — panel deslizable desde la derecha */}
        <div className={`mobile-menu ${mobileMenuOpen ? "open" : ""}`} onClick={() => setMobileMenuOpen(false)}>
          <div className="mobile-menu-panel" onClick={e => e.stopPropagation()}>
            <div className="mobile-menu-head">
              <img src="/litislab-logo.png" alt="LitisLab" className="mobile-menu-logo" />
              <button className="mobile-menu-close" aria-label="Cerrar menú" onClick={() => setMobileMenuOpen(false)}>✕</button>
            </div>

            <div className="mobile-menu-links">
              <a href="#funcionalidades" onClick={() => setMobileMenuOpen(false)}>Funcionalidades</a>
              <a href="#proceso" onClick={() => setMobileMenuOpen(false)}>Cómo funciona</a>
              <a href="#precios" onClick={() => setMobileMenuOpen(false)}>Precios</a>
              <a href="#faq" onClick={() => setMobileMenuOpen(false)}>FAQ</a>
            </div>

            <div className="mobile-menu-team">
              <div className="mobile-menu-label">Color de tema</div>
              <div className="mobile-menu-team-row">
                <button
                  className={`mobile-team-card ${team === "orange" ? "active" : ""}`}
                  onClick={() => { changeTeam("orange"); }}
                >
                  <span className="mobile-team-dot" style={{ background: "#C4572A" }} />
                  <span>Naranja</span>
                </button>
                <button
                  className={`mobile-team-card ${team === "green" ? "active" : ""}`}
                  onClick={() => { changeTeam("green"); }}
                >
                  <span className="mobile-team-dot" style={{ background: "#70A33C" }} />
                  <span>Verde</span>
                </button>
              </div>
            </div>

            <div className="mobile-menu-actions">
              <button className="mobile-menu-btn-ghost" onClick={() => { setMobileMenuOpen(false); openAuth("login")(); }}>
                Iniciar sesión
              </button>
              <button className="mobile-menu-btn-dark" onClick={() => { setMobileMenuOpen(false); openAuth("register")(); }}>
                Comenzar gratis →
              </button>
            </div>
          </div>
        </div>

        {/* HERO */}
        <div className="hero">
          <h1 className="hero-title">
            Practica litigación oral<br />
            <span className="accent">contra una IA implacable</span>
          </h1>
          <p className="hero-sub">
            La única plataforma para dominar el sistema acusatorio oral penal de Ecuador. Interrogatorios, contrainterrogatorios y alegatos bajo el COIP — con retroalimentación en tiempo real.
          </p>
          <div className="hero-actions">
            <button className="btn-orange btn-arrow" onClick={openAuth("register")}>Comenzar práctica gratuita</button>
            <a href="#proceso" className="btn-outline">Ver cómo funciona</a>
          </div>
        </div>

        {/* HERO VISUAL */}
        <div className="hero-visual-wrap">
          <div className="hero-visual reveal">
            <div className="hero-visual-bar">
              <span className="window-dot" style={{ background: "#FF5F57" }} />
              <span className="window-dot" style={{ background: "#FFBD2E" }} />
              <span className="window-dot" style={{ background: "#28C840" }} />
              <div className="hero-visual-url">litislab.com — Sala de Simulación</div>
            </div>
            <div className="app-mock">
              <div className="app-topbar">
                <div className="app-topbar-logo">
                  <span className="brand-mark">Litis<span>Lab</span></span>
                  <span className="app-topbar-case">Lesiones con botella rota · Art. 152 COIP · Guayaquil, 14 feb 2025</span>
                </div>
                <div className="app-topbar-right">
                  <span className="app-badge badge-int">Interrogatorio</span>
                  <span className="app-badge badge-timer">2/4 · Augusto&nbsp;&nbsp;00:29</span>
                </div>
              </div>
              <div className="app-sidebar">
                <div>
                  <div className="sidebar-section-label">En estrado</div>
                  <div className="witness-active">
                    <div className="witness-name">Augusto Temujin</div>
                    <div className="witness-role">Perito técnico</div>
                    <span className="witness-tag">Cooperativo</span>
                  </div>
                </div>
                <div>
                  <div className="sidebar-section-label">Testigos</div>
                  <div className="witness-list">
                    <div className="witness-item"><span className="witness-dot" style={{ background: "var(--green)" }} />Augusto Temujin</div>
                    <div className="witness-item"><span className="witness-dot" style={{ background: "var(--muted)" }} />Cleopatra Quispe</div>
                    <div className="witness-item"><span className="witness-dot" style={{ background: "var(--muted)" }} />Rodrigo Huanca</div>
                    <div className="witness-item"><span className="witness-dot" style={{ background: "var(--orange)" }} />Teodora Montoya</div>
                  </div>
                </div>
                <div>
                  <div className="sidebar-section-label">Teoría · Fiscalía</div>
                  <div className="theory-box">
                    <div className="theory-label">Fiscalía</div>
                    Isabel Zhao esperó a su cuñado a la salida del trabajo y lo hirió con una botella de vidrio rota. Sangre B+ en la botella incautada.
                  </div>
                </div>
              </div>
              <div className="app-main">
                <div className="transcript">
                  <div className="t-msg">
                    <div className="t-role sec">Secretaría</div>
                    <div className="t-text">Se instala el Tribunal. Causa N.º 17282-2025-00341, delito de lesiones tipificado en el Art. 152 del COIP. Comparece Fiscalía: Dr. Joseph W. Ramos; Defensa: Dra. Mónica Cevallos.</div>
                  </div>
                  <div className="t-msg">
                    <div className="t-role fis">Fiscal · Interrogatorio directo</div>
                    <div className="t-text">Señor Temujin, ¿cuántas manchas hemáticas examinó en la escena y qué patrón de dispersión determinó?</div>
                  </div>
                  <div className="t-msg">
                    <div className="t-role test">Perito · Augusto Temujin</div>
                    <div className="t-text">Examiné 11 manchas. El patrón es de goteo lineal con 2,8 metros de dispersión. <em>El grupo sanguíneo es B positivo, coincidente con el tipo de la víctima.</em></div>
                  </div>
                </div>
                <div className="app-input">
                  <div className="app-input-field">La contraparte interroga...</div>
                  <button className="app-send">Enviar</button>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* TRUSTED BY STRIP */}
        <div className="logos-strip">
          <p className="logos-strip-label">Diseñado para profesionales del derecho penal ecuatoriano</p>
          <div className="logos-row">
            <span className="logo-pill">Abogados penalistas</span>
            <span className="logo-pill">Firmas de litigación</span>
            <span className="logo-pill">Facultades de Derecho</span>
            <span className="logo-pill">Fiscalías</span>
            <span className="logo-pill">Defensoría Pública</span>
          </div>
        </div>

        <hr className="section-divider" />

        {/* FEATURE 1 */}
        <section id="funcionalidades">
          <div className="feature-section reveal">
            <div className="feature-text">
              <span className="feature-tag">Sala de Simulación</span>
              <h2 className="feature-title">Un tribunal virtual que te hace mejores preguntas</h2>
              <p className="feature-desc">Configura tu rol — fiscal o defensor — elige la dificultad y entra a sala. La IA actúa como testigo, perito y contraparte. Cada sesión es un caso distinto generado al momento.</p>
              <button className="btn-dark-sm btn-arrow" onClick={openAuth("register")}>Probar gratis</button>
            </div>
            <div className="feature-visual">
              <div className="fv-bar">
                <span className="window-dot" style={{ background: "#FF5F57" }} />
                <span className="window-dot" style={{ background: "#FFBD2E" }} />
                <span className="window-dot" style={{ background: "#28C840" }} />
              </div>
              <div className="fv-body">
                <div className="setup-steps">
                  <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", color: "var(--light)", marginBottom: 8 }}>
                    Nueva Simulación
                  </div>
                  <div className="setup-step active">
                    <div className="setup-step-num">1</div>
                    <div>
                      <div className="setup-step-label">Tipo de delito</div>
                      <div className="setup-step-sub">Art. 152 COIP · Lesiones</div>
                    </div>
                  </div>
                  <div className="setup-choices">
                    <div className="setup-choice sel">
                      <div className="setup-choice-title">Fiscal del Estado</div>
                      <div className="setup-choice-sub">Presentas testigos primero</div>
                    </div>
                    <div className="setup-choice">
                      <div className="setup-choice-title">Defensor Penal</div>
                      <div className="setup-choice-sub">La IA presenta primero</div>
                    </div>
                  </div>
                  <div className="setup-step">
                    <div className="setup-step-num">3</div>
                    <div>
                      <div className="setup-step-label">Dificultad</div>
                      <div className="setup-step-sub">Intermedio · Caso equilibrado</div>
                    </div>
                  </div>
                  <div className="setup-step">
                    <div className="setup-step-num">4</div>
                    <div>
                      <div className="setup-step-label">Generar expediente con IA</div>
                      <div className="setup-step-sub">Casos únicos cada vez</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        <hr className="section-divider" />

        {/* FEATURE 2 */}
        <section>
          <div className="feature-section reverse reveal">
            <div className="feature-text">
              <span className="feature-tag">Expediente con IA</span>
              <h2 className="feature-title">Casos reales generados al instante con el COIP</h2>
              <p className="feature-desc">Cada práctica parte de un expediente único: hechos, víctimas, testigos, pericias, contradicciones. Teorías contrapuestas entre fiscalía y defensa. Nunca el mismo caso dos veces.</p>
              <button className="btn-dark-sm btn-arrow" onClick={openAuth("register")}>Ver ejemplo de caso</button>
            </div>
            <div className="feature-visual">
              <div className="fv-bar">
                <span className="window-dot" style={{ background: "#FF5F57" }} />
                <span className="window-dot" style={{ background: "#FFBD2E" }} />
                <span className="window-dot" style={{ background: "#28C840" }} />
              </div>
              <div className="fv-body">
                <div className="case-doc">
                  <div className="case-doc-title">Lesiones con botella rota en Sauces 6, Guayaquil</div>
                  <div className="case-doc-meta">Procesada: Isabel Zhao &nbsp;·&nbsp; Víctima: Julio Ragnar &nbsp;·&nbsp; 14 feb 2025</div>
                  <div className="case-doc-body">
                    El 14 de febrero a las 17:50, en la acera frente a un local de fotocopiado, Isabel Zhao habría interceptado a su cuñado y lo hirió con una botella de vidrio rota. Se hallaron 11 manchas hemáticas con patrón de goteo de 2,8 m. La víctima presentó herida cortante en región malar de 4,7 cm y 12 días de incapacidad...
                  </div>
                  <div className="theory-grid">
                    <div className="theory-card">
                      <div className="theory-card-label fis">Fiscalía</div>
                      <div className="theory-card-text">Agresión premeditada. Sangre B+ en botella incautada, video y dos testigos oculares ubican a la procesada.</div>
                    </div>
                    <div className="theory-card">
                      <div className="theory-card-label def">Defensa</div>
                      <div className="theory-card-text">Forcejeo mutuo. Sin ADN individualizado, sin video completo del momento del corte. Herida compatible con caída.</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        <hr className="section-divider" />

        {/* FEATURE 3 */}
        <section id="proceso">
          <div className="feature-section reveal">
            <div className="feature-text">
              <span className="feature-tag">Evaluación en tiempo real</span>
              <h2 className="feature-title">Retroalimentación que te dice exactamente dónde fallaste</h2>
              <p className="feature-desc">Al finalizar cada sesión recibes un informe detallado: técnica de interrogatorio, manejo de prueba, estrategia procesal e impacto en sala. No intuición — datos concretos de mejora.</p>
              <button className="btn-dark-sm btn-arrow" onClick={openAuth("register")}>Ver informe de ejemplo</button>
            </div>
            <div className="feature-visual">
              <div className="fv-bar">
                <span className="window-dot" style={{ background: "#FF5F57" }} />
                <span className="window-dot" style={{ background: "#FFBD2E" }} />
                <span className="window-dot" style={{ background: "#28C840" }} />
              </div>
              <div className="fv-body">
                <div className="score-report">
                  <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", color: "var(--light)", marginBottom: 4 }}>
                    Informe de sesión · Caso N.º 17282
                  </div>
                  <div className="score-row">
                    <div className="score-row-top"><span className="score-row-label">Técnica de interrogatorio</span><span className="score-row-val">82</span></div>
                    <div className="score-bar"><div className="score-fill" style={{ width: "82%", background: "var(--green)" }} /></div>
                    <div className="score-comment">Buena apertura. Evitar preguntas compuestas en el segundo turno.</div>
                  </div>
                  <div className="score-row">
                    <div className="score-row-top"><span className="score-row-label">Manejo de prueba pericial</span><span className="score-row-val">75</span></div>
                    <div className="score-bar"><div className="score-fill" style={{ width: "75%", background: "#5A8FC4" }} /></div>
                    <div className="score-comment">No impugnaste la metodología del perito. Oportunidad perdida.</div>
                  </div>
                  <div className="score-row">
                    <div className="score-row-top"><span className="score-row-label">Estrategia procesal</span><span className="score-row-val">91</span></div>
                    <div className="score-bar"><div className="score-fill" style={{ width: "91%", background: "var(--orange)" }} /></div>
                    <div className="score-comment">Excelente secuencia de testigos. Contradicción expuesta con precisión.</div>
                  </div>
                  <div className="score-total">
                    <span className="score-total-label">Puntuación total</span>
                    <span className="score-total-val">83 / 100</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        <hr className="section-divider" />

        {/* TESTIMONIALS — duplicados para loop infinito */}
        <section className="testimonials">
          <div className="testimonials-header reveal">
            <h2 className="section-title">Lo que dicen quienes <span className="accent">ya practican</span></h2>
            <p className="section-sub">Abogados, estudiantes y firmas que usan LitisLab para prepararse en serio.</p>
          </div>
          <div className="testimonials-track">
            {[...TESTIMONIALS, ...TESTIMONIALS].map((t, i) => (
              <div key={i} className="testimonial-card">
                <div className="test-avatar" style={{ background: t.color }}>{t.letter}</div>
                <div className="test-quote">&ldquo;</div>
                <div className="test-text">{t.text}</div>
                <div className="test-byline">
                  <div>
                    <div className="test-name">{t.name}</div>
                    <div className="test-role">{t.role}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>

        <hr className="section-divider" />

        {/* FAQ */}
        <section id="faq">
          <div className="faq-section">
            <h2 className="faq-title reveal">Preguntas frecuentes</h2>
            {FAQ_ITEMS.map((item, i) => (
              <div key={i} className={`faq-item ${openFaqIdx === i ? "open" : ""}`}>
                <button className="faq-q" onClick={() => toggleFaq(i)}>
                  {item.q}<span className="faq-icon">+</span>
                </button>
                <div className="faq-a">{item.a}</div>
              </div>
            ))}
          </div>
        </section>

        {/* CTA */}
        <div className="cta-section reveal" id="precios">
          <h2 className="cta-title">Tu próximo juicio<br />empieza <span className="accent">hoy</span></h2>
          <p className="cta-sub">Sin improvisación. Con la preparación que solo un adversario implacable puede darte.</p>
          <div className="cta-actions">
            <button className="btn-orange btn-arrow" onClick={openAuth("register")}>Acceder gratis</button>
            <a href="mailto:hola@litislab.com" className="cta-link-secondary">Hablar con el equipo</a>
          </div>
        </div>

        {/* FOOTER */}
        <footer>
          <div className="footer-top">
            <div className="footer-brand">
              <img src="/litislab-logo-light.png" alt="LitisLab" className="footer-brand-img" />
              <div className="footer-tagline">Plataforma de práctica de litigación oral penal bajo el COIP. Ecuador, 2026.</div>
            </div>
            <div>
              <div className="footer-col-title">Producto</div>
              <div className="footer-col-links">
                <a href="#funcionalidades">Funcionalidades</a>
                <a href="#proceso">Cómo funciona</a>
                <a href="#precios">Precios</a>
                <button type="button">Blog</button>
              </div>
            </div>
            <div>
              <div className="footer-col-title">Compañía</div>
              <div className="footer-col-links">
                <button type="button">Acerca de</button>
                <a href="mailto:hola@litislab.com">Contacto</a>
                <button type="button">Términos de uso</button>
                <button type="button">Privacidad</button>
              </div>
            </div>
            <div>
              <div className="footer-col-title">Social</div>
              <div className="footer-col-links">
                <a href="https://www.linkedin.com" target="_blank" rel="noopener noreferrer">LinkedIn</a>
                <a href="https://x.com" target="_blank" rel="noopener noreferrer">X / Twitter</a>
                <a href="https://www.instagram.com" target="_blank" rel="noopener noreferrer">Instagram</a>
              </div>
            </div>
          </div>
          <div className="footer-bottom">
            <span className="footer-copy">©2026 LitisLab</span>
            <div className="footer-bottom-links">
              <button type="button">Términos</button>
              <button type="button">Privacidad</button>
              <button type="button">Español (Ecuador)</button>
            </div>
          </div>
        </footer>
      </div>

      {/* AuthModal — se mantiene igual al actual; cuando construyamos /login lo reemplazamos */}
      {authOpen && (
        <AuthModal
          mode={authMode}
          onClose={() => setAuthOpen(false)}
        />
      )}
    </>
  );
}
